/**
 * Parse a pasted curl command into request fields.
 * Returns null if the text is not a recognizable curl command with a URL
 * (http/https, or one that starts with a `{{variable}}` as Postman exports it).
 */

import { splitUrlAndParams } from './urlParams';

export { splitUrlAndParams };

/**
 * Chrome's "Copy as cURL (cmd)" escapes every special character with `^` and
 * continues lines with a trailing `^`. Undo that so the rest of the parser sees
 * a plain double-quoted command. Only applied when the text actually looks like
 * cmd syntax, so a `^` inside a bash-quoted body is left alone.
 */
function unescapeWindowsCmd(text) {
  if (!/\^"/.test(text) && !/\^[ \t]*\r?\n/.test(text)) return text;
  return text
    .replace(/\^\r?\n\r?\n/g, '\n') // an escaped newline inside a quoted value
    .replace(/\^[ \t]*\r?\n/g, ' ') // line continuation
    .replace(/\^(.)/g, '$1');
}

function normalizeCurlInput(text) {
  if (!text || typeof text !== 'string') return '';
  return unescapeWindowsCmd(text).replace(/\\\r?\n/g, ' ').replace(/\\\n/g, ' ').trim();
}

/** Split remainder after `curl` into argv-style tokens (double/single quoted segments). */
export function tokenizeShellLike(str) {
  const tokens = [];
  let i = 0;
  const len = str.length;
  while (i < len) {
    while (i < len && /\s/.test(str[i])) i++;
    if (i >= len) break;
    // $'...' ANSI-C quoting: treat like single-quotes with basic escape handling
    if (str[i] === '$' && i + 1 < len && str[i + 1] === "'") {
      i += 2;
      let buf = '';
      while (i < len) {
        if (str[i] === '\\' && i + 1 < len) {
          const esc = str[++i];
          const escMap = { n: '\n', t: '\t', r: '\r', '\\': '\\', "'": "'", '"': '"' };
          buf += esc in escMap ? escMap[esc] : '\\' + esc;
          i++;
          continue;
        }
        if (str[i] === "'") { i++; break; }
        buf += str[i++];
      }
      tokens.push(buf);
    } else if (str[i] === '"' || str[i] === "'") {
      const quote = str[i++];
      let buf = '';
      while (i < len) {
        if (quote === '"' && str[i] === '\\' && i + 1 < len) {
          buf += str[++i];
          i++;
          continue;
        }
        if (str[i] === quote) {
          i++;
          break;
        }
        buf += str[i++];
      }
      tokens.push(buf);
    } else {
      let buf = '';
      while (i < len && !/\s/.test(str[i])) buf += str[i++];
      tokens.push(buf);
    }
  }
  return tokens;
}

function getContentType(headers) {
  const h = headers.find((x) => x.key.toLowerCase() === 'content-type');
  return h ? h.value.split(';')[0].trim().toLowerCase() : '';
}

function parseCookieFlagValue(s) {
  const pairs = [];
  const trimmed = s.trim();
  if (!trimmed || trimmed.startsWith('@')) return pairs;
  for (const part of trimmed.split(';')) {
    const p = part.trim();
    const eq = p.indexOf('=');
    if (eq === -1) continue;
    pairs.push({
      key: p.slice(0, eq).trim(),
      value: p.slice(eq + 1).trim(),
    });
  }
  return pairs;
}

function inferBodyType(headers, body) {
  const ct = getContentType(headers);
  if (ct.includes('json')) return 'json';
  if (ct.includes('x-www-form-urlencoded')) return 'form';
  if (body && /^\s*[[{]/.test(body)) return 'json';
  return 'raw';
}

const URL_RE = /^https?:\/\//i;
/** Postman exports `{{HostName}}/path` (or `{{scheme}}://…`) when the base URL is a variable. */
const VAR_URL_RE = /^\{\{[^{}\s]+\}\}/;

function looksLikeUrl(token) {
  return URL_RE.test(token) || VAR_URL_RE.test(token);
}

/**
 * Flags that are accepted and have no effect on the imported request.
 * Listed so that it is explicit they are handled, not forgotten.
 */
const IGNORED_FLAGS = new Set([
  '--globoff', '-g', '--location', '-L', '--location-trusted', '--compressed',
  '-k', '--insecure', '-s', '--silent', '-S', '--show-error', '-v', '--verbose',
  '-i', '--include', '-f', '--fail', '--http1.1', '--http2', '--http2-prior-knowledge',
  '-#', '--progress-bar', '-N', '--no-buffer',
]);

/**
 * Flags that take a value which is NOT part of the request we import. Their
 * value must be skipped, or a value like `-e https://referrer/` would be
 * mistaken for the request URL.
 */
const VALUE_FLAGS_SKIPPED = new Set([
  '-o', '--output', '-e', '--referer', '-x', '--proxy', '-U', '--proxy-user',
  '-m', '--max-time', '--connect-timeout', '--retry', '--retry-delay', '--retry-max-time',
  '-w', '--write-out', '--cacert', '--capath', '--cert', '-E', '--key', '--cert-type',
  '--key-type', '--resolve', '--connect-to', '--max-redirs', '-c', '--cookie-jar',
  '-D', '--dump-header', '--limit-rate', '-y', '--speed-time', '-Y', '--speed-limit',
  '--interface', '--dns-servers', '-K', '--config', '--trace', '--trace-ascii',
]);

function decodeFormComponent(s) {
  const plus = s.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(plus);
  } catch {
    return plus;
  }
}

/** `a=1&b=2` → rows (decoded). A fragment without `=` becomes a key with an empty value. */
function rowsFromEncodedFragment(fragment) {
  const rows = [];
  for (const part of fragment.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    rows.push(
      eq === -1
        ? { key: decodeFormComponent(part), value: '' }
        : { key: decodeFormComponent(part.slice(0, eq)), value: decodeFormComponent(part.slice(eq + 1)) }
    );
  }
  return rows;
}

/**
 * One `--data-urlencode` argument, per curl's rules:
 *   content | =content | name=content | @file | name@file
 * Returns { rows, encoded } or { file: true } for the file forms (not importable).
 */
function parseDataUrlencode(arg) {
  const eq = arg.indexOf('=');
  const at = arg.indexOf('@');
  if (at !== -1 && (eq === -1 || at < eq)) return { file: true };
  if (eq === -1) {
    return { rows: [{ key: arg, value: '' }], encoded: encodeURIComponent(arg) };
  }
  const name = arg.slice(0, eq);
  const content = arg.slice(eq + 1);
  if (name === '') {
    return { rows: [{ key: content, value: '' }], encoded: encodeURIComponent(content) };
  }
  return { rows: [{ key: name, value: content }], encoded: `${name}=${encodeURIComponent(content)}` };
}

/**
 * One `-F` argument: `name=value`, `name=@file[;type=…]`, `name=<file`.
 * `--form-string` values are literal, so `@`/`<` mean nothing there.
 */
function parseFormFlag(arg, literal = false) {
  const eq = arg.indexOf('=');
  if (eq === -1) return null;
  const key = arg.slice(0, eq);
  const value = arg.slice(eq + 1);
  if (!literal && (value.startsWith('@') || value.startsWith('<'))) {
    return { key, value: '', type: 'file', file: true };
  }
  return { key, value, type: 'text' };
}

function decodeBase64(s) {
  try {
    const bin = typeof atob === 'function' ? atob(s) : Buffer.from(s, 'base64').toString('binary');
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

const VAR_PAIR_RE = /^(\{\{[^{}\s]+\}\}):(\{\{[^{}\s]+\}\})$/;

/**
 * `Authorization: Basic …` whose credentials are exactly `{{a}}:{{b}}` (as a
 * literal, or base64 of that literal as Postman exports it) → basic auth with
 * the variables kept. Anything else (real credentials) stays a header.
 */
function basicAuthFromHeader(value) {
  const m = /^Basic\s+(\S+)\s*$/i.exec(value);
  if (!m) return null;
  const candidates = [m[1], decodeBase64(m[1])];
  for (const c of candidates) {
    const pair = c && VAR_PAIR_RE.exec(c);
    if (pair) return { username: pair[1], password: pair[2] };
  }
  return null;
}

/**
 * @returns {{
 *   method: string,
 *   url: string,
 *   headers: { key: string, value: string }[],
 *   params: { key: string, value: string }[],
 *   cookies: { key: string, value: string }[],
 *   body: string | null,
 *   bodyType: 'json' | 'form' | 'formdata' | 'raw',
 *   formFields: { key: string, value: string, type: 'text'|'file' }[] | null,
 *   auth: { type: 'basic', basic: { username: string, password: string } } | null,
 *   skippedFileBody: boolean
 * } | null}
 */
export function parseCurl(text) {
  const normalized = normalizeCurlInput(text);
  if (!normalized || !/^\s*curl(?:\.exe)?\s/i.test(normalized)) return null;

  const lead = normalized.match(/^\s*curl(?:\.exe)?\s+/i);
  if (!lead) return null;

  const rest = normalized.slice(lead[0].length);
  const tokens = tokenizeShellLike(rest);
  if (tokens.length === 0) return null;

  let method = 'GET';
  let explicitMethod = false;
  let url = null;
  const headers = [];
  const cookies = [];
  // Each data argument keeps its wire form (`encoded`) and its rows (decoded).
  const dataParts = [];
  const formParts = [];
  let usedDataUrlencode = false;
  let getMode = false;
  let auth = null;
  let skippedFileBody = false;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const hasNext = i + 1 < tokens.length;

    if (t === '-X' || t === '--request') {
      if (hasNext) {
        method = tokens[++i].toUpperCase();
        explicitMethod = true;
      }
      continue;
    }
    if (t === '-H' || t === '--header') {
      if (hasNext) {
        const hv = tokens[++i];
        const colon = hv.indexOf(':');
        if (colon !== -1) {
          headers.push({
            key: hv.slice(0, colon).trim(),
            value: hv.slice(colon + 1).trim(),
          });
        }
      }
      continue;
    }
    if (t === '-d' || t === '--data' || t === '--data-raw' || t === '--data-binary' || t === '--data-ascii') {
      if (hasNext) {
        const data = tokens[++i];
        if (data.startsWith('@') && t !== '--data-raw') {
          skippedFileBody = true;
        } else {
          dataParts.push({ encoded: data, rows: null });
        }
      }
      continue;
    }
    if (t === '--data-urlencode') {
      if (hasNext) {
        const parsed = parseDataUrlencode(tokens[++i]);
        if (parsed.file) {
          skippedFileBody = true;
        } else {
          usedDataUrlencode = true;
          dataParts.push(parsed);
        }
      }
      continue;
    }
    if (t === '-F' || t === '--form' || t === '--form-string') {
      if (hasNext) {
        const row = parseFormFlag(tokens[++i], t === '--form-string');
        if (row) {
          if (row.file) skippedFileBody = true;
          formParts.push({ key: row.key, value: row.value, type: row.type });
        }
      }
      continue;
    }
    if (t === '-u' || t === '--user') {
      if (hasNext) {
        const cred = tokens[++i];
        const colon = cred.indexOf(':');
        auth = {
          type: 'basic',
          basic: colon === -1
            ? { username: cred, password: '' }
            : { username: cred.slice(0, colon), password: cred.slice(colon + 1) },
        };
      }
      continue;
    }
    if (t === '-G' || t === '--get') {
      getMode = true;
      continue;
    }
    if (t === '--url') {
      if (hasNext) {
        const u = tokens[++i];
        if (!url) url = u;
      }
      continue;
    }
    if (t === '-b' || t === '--cookie') {
      if (hasNext) {
        const cv = tokens[++i];
        cookies.push(...parseCookieFlagValue(cv));
      }
      continue;
    }
    if (t === '-A' || t === '--user-agent') {
      if (hasNext) headers.push({ key: 'User-Agent', value: tokens[++i] });
      continue;
    }
    if (VALUE_FLAGS_SKIPPED.has(t)) {
      if (hasNext) i++;
      continue;
    }
    if (IGNORED_FLAGS.has(t)) continue;
    if (looksLikeUrl(t)) {
      if (!url) url = t;
      continue;
    }
  }

  // Fallback: token order / quoting sometimes skips the URL (still valid curl with headers/body).
  if (!url) {
    const m = normalized.match(/\bhttps?:\/\/[^\s\\'"<>]+/i);
    if (m) {
      url = m[0].replace(/[)\],;.]+$/u, '');
    }
  }

  if (!url) return null;

  // `Authorization: Basic {{a}}:{{b}}` → the auth tab (only when -u did not already set it).
  if (!auth) {
    const idx = headers.findIndex((h) => h.key.toLowerCase() === 'authorization');
    if (idx !== -1) {
      const basic = basicAuthFromHeader(headers[idx].value);
      if (basic) {
        auth = { type: 'basic', basic };
        headers.splice(idx, 1);
      }
    }
  }

  const ct = getContentType(headers);
  const rowsOf = (part) => part.rows || rowsFromEncodedFragment(part.encoded);

  let body = null;
  let bodyType;
  let formFields = null;
  let { baseUrl, params } = splitUrlAndParams(url);

  if (getMode && dataParts.length > 0) {
    // -G: the data goes on the query string, not in a body.
    params = [...params, ...dataParts.flatMap(rowsOf)];
    bodyType = 'raw';
  } else if (formParts.length > 0) {
    formFields = formParts;
    bodyType = 'formdata';
    if (!explicitMethod) method = 'POST';
  } else {
    const isForm =
      ct.includes('application/x-www-form-urlencoded') || (!ct && usedDataUrlencode);
    if (dataParts.length > 0) {
      if (isForm) {
        body = dataParts.map((p) => p.encoded).join('&');
        formFields = dataParts.flatMap(rowsOf).map((r) => ({ ...r, type: 'text' }));
      } else {
        body = dataParts[dataParts.length - 1].encoded;
      }
    }
    if (method === 'GET' && body) {
      method = 'POST';
    }
    bodyType = isForm && body ? 'form' : inferBodyType(headers, body);
  }

  return {
    method,
    url: baseUrl,
    headers,
    params,
    cookies,
    body,
    bodyType,
    formFields,
    auth,
    skippedFileBody,
  };
}
